import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from '@angular/core';
import {
    docSnapshots,
    collectionData,
    collectionSnapshots,
    query,
    QueryDocumentSnapshot,
    doc,
    setDoc,
    updateDoc,
    getDocFromServer,
    QueryConstraint,
    collection,
    getDocsFromServer,
    FirestoreInstances,
    deleteDoc,
    addDoc,
    DocumentReference,
    Firestore,
    CollectionReference,
    DocumentData,
    where,
} from '@angular/fire/firestore';

import { Observable, map, filter } from 'rxjs';
import { Client } from 'sharedTypes/client';

@Injectable({
    providedIn: 'root',
})
export class FirestoreService {
    private injectionContext = inject(EnvironmentInjector);
    #firestore: Firestore;
    logsFirestore: Firestore;

    constructor(public allFirestoreDbs: FirestoreInstances) {
        this.#firestore = this.allFirestoreDbs[0];
        this.logsFirestore = this.allFirestoreDbs[1];
        console.log('FirestoreService initialized with Firestore instances:', this.#firestore, this.logsFirestore);
    }

    createId(): string {
        const ref = collection(this.#firestore, '_');
        return doc(ref).id;
    }

    streamDocumentData<T>(path: string): Observable<T> {
        return runInInjectionContext(this.injectionContext, () => {
            return docSnapshots(doc(this.#firestore, path)).pipe(map((element) => element.data() as T));
        });
    }

    async setFirestoreDocument<T>(
        path: string,
        data: T,
        merge: boolean = false,
        firestoreDb = this.#firestore,
    ): Promise<void> {
        console.log('setFirestoreDocument', path, data, merge);
        const object2save = JSON.parse(JSON.stringify(data));
        const dbref = doc(firestoreDb, path);
        let options = {};
        if (merge) {
            options = { merge: true };
        }
        return setDoc(dbref, object2save, options);
    }

    async updateFirestoreDocument<T>(path: string, data: Partial<T>, firestoreDb = this.#firestore): Promise<void> {
        console.log('updateFirestoreDocument', path, data);
        const updates2save = JSON.parse(JSON.stringify(data));
        const dbref = doc(firestoreDb, path);

        return updateDoc(dbref, updates2save);
    }

    async addToFirestoreCollection<T>(
        path: string,
        data: Partial<T>,
        firestoreDb = this.#firestore,
    ): Promise<DocumentReference<T>> {
        console.log('addToFirestoreCollection', path, data);
        const collectionRef = collection(firestoreDb, path) as CollectionReference<T>;
        return addDoc(collectionRef, data as T);
    }

    getClientsWithGroupId() {
        return this.streamCollectionData<Client>('clients', [where('groupId', '!=', null)]);
    }

    async deleteFirestoreDocument<T>(path: string, firestoreDb = this.#firestore): Promise<void> {
        console.log('deleteFirestoreDocument', path);
        const dbref = doc(firestoreDb, path);

        return deleteDoc(dbref);
    }

    async fetchDocumentData<T>(path: string, firestoreDb = this.#firestore): Promise<T | null> {
        console.log('fetchDocumentData', path);
        return runInInjectionContext(this.injectionContext, async () => {
            const document = await getDocFromServer(doc(firestoreDb, path));
            const data = document.data() as T;

            if (!data) {
                console.error('Document ' + path + ' does not exist!');

                return null;
            }

            return data;
        });
    }

    streamCollectionData<T>(
        path: string,
        queryConstraints: QueryConstraint[] = [],
        firestoreDb = this.#firestore,
    ): Observable<T[]> {
        return runInInjectionContext(this.injectionContext, () => {
            const ref = collection(firestoreDb, path);
            const q = query(ref, ...queryConstraints);

            return collectionData(q) as Observable<T[]>;
        });
    }

    streamCollectionDataNoCache<T>(
        path: string,
        queryConstraints: QueryConstraint[] = [],
        firestoreDb = this.#firestore,
    ): Observable<T[]> {
        console.log('streamCollectionDataNoCache', path, queryConstraints);
        return runInInjectionContext(this.injectionContext, () => {
            const ref = collection(firestoreDb, path);
            const q = query(ref, ...queryConstraints);

            return collectionSnapshots(q).pipe(
                filter((snapshot) => snapshot.length === 0 || snapshot.every((snap) => !snap.metadata.fromCache)),
                map((snapshot) => snapshot.map((snap) => snap.data() as T)),
            );
        });
    }

    async fetchCollectionData<T>(
        path: string,
        queryConstraints: QueryConstraint[] = [],
        firestoreDb = this.#firestore,
    ): Promise<T[]> {
        return runInInjectionContext(this.injectionContext, async () => {
            const ref = collection(firestoreDb, path);
            const q = query(ref, ...queryConstraints);

            const docSnapshots = await getDocsFromServer(q);
            const docs: T[] = [];

            docSnapshots.forEach((snapshot) => {
                docs.push(snapshot.data() as T);
            });

            return docs;
        });
    }

    streamCollectionSnapshots<T>(
        path: string,
        queryConstraints: QueryConstraint[] = [],
        firestoreDb = this.#firestore,
    ): Observable<QueryDocumentSnapshot<T, DocumentData>[]> {
        console.log('streamCollectionSnapshots', path, queryConstraints);
        return runInInjectionContext(this.injectionContext, () => {
            const ref = collection(firestoreDb, path);
            const q = query(ref, ...queryConstraints);

            return collectionSnapshots(q) as Observable<QueryDocumentSnapshot<T, DocumentData>[]>;
        });
    }
}
